export type Operation = 'insert' | 'remove' | 'equal';

export interface Change {
  operation: Operation;
  value: string;
}

/**
 * Compute Levenshtein edit distance between two strings.
 */
export function editDistance(s1: string, s2: string): number {
  const len1 = s1.length;
  const len2 = s2.length;
  const dp: number[][] = new Array(len1 + 1).fill(0).map(() => new Array(len2 + 1));
  for (let i = 0; i <= len1; i++) dp[i][0] = i;
  for (let j = 0; j <= len2; j++) dp[0][j] = j;
  for (let i = 0; i < len1; i++) {
    for (let j = 0; j < len2; j++) {
      if (s1[i] === s2[j]) {
        dp[i + 1][j + 1] = dp[i][j];
      } else {
        dp[i + 1][j + 1] = 1 + Math.min(dp[i][j], dp[i][j + 1], dp[i + 1][j]);
      }
    }
  }
  return dp[len1][len2];
}

/*
  * Computing Levenshtein edit distance using dynamic programming.
  * Allowed operations are:
  *      => insert (cost 1)
  *      => delete (cost 1)
  *      could add replace (cost 1) but it's not necessary
*/
function diff(s1: string[], s2: string[]) {
  const len1 = s1.length;
  const len2 = s2.length;
  const dp: number[][] = new Array(len1 + 1).fill(0).map(() => new Array(len2 + 1));
  for (let i = 0; i <= len1; i++) {
    dp[i][0] = i;
  }

  for (let j = 0; j <= len2; j++) {
    dp[0][j] = j;
  }
  for (let i = 0; i < len1; i++) {
    const c1 = s1[i];
    for (let j = 0; j < len2; j++) {
      const c2 = s2[j];
      // if last two chars equal
      if (c1 === c2) {
        // update dp value for + 1 length
        dp[i + 1][j + 1] = dp[i][j];
      } else {
        const insert = dp[i][j + 1] + 1;
        const removed = dp[i + 1][j] + 1;
        dp[i + 1][j + 1] = Math.min(insert, removed);
      }
    }
  }
  return dp;
}
export function diffText(s1: string, s2: string) {
  // start with word diff and if distance is too big, use sentence diff
  const delimiter = ' ';
  const a = s1.split(delimiter).filter((x) => x.length > 0);
  const b = s2.split(delimiter).filter((x) => x.length > 0);
  const wordDistance = diff(a, b);
  const distance = wordDistance[a.length][b.length];
  const maxLength = Math.max(a.length, b.length);
  if (distance > maxLength / 3) {
    return diffSentences(s1, s2);
  }
  return backtrack(a, b, wordDistance, delimiter);
}

export function diffChars(s1: string, s2: string) {
  return diffChanges(s1, s2, '');
}
export function diffWords(s1: string, s2: string) {
  return diffChanges(s1, s2, ' ');
}
export function diffSentences(s1: string, s2: string) {
  // This is good enough for now
  return diffChanges(s1, s2, '.');
}
function diffChanges(s1: string, s2: string, delimiter = '') {
  const text1 = s1.split(delimiter).filter((x) => x.length > 0);
  const text2 = s2.split(delimiter).filter((x) => x.length > 0);
  const dp = diff(text1, text2);
  return backtrack(text1, text2, dp, delimiter);
}
function backtrack(s1: string[], s2: string[], dp: number[][], delimiter = '') {
  const len1 = s1.length;
  const len2 = s2.length;
  const changes: Change[] = [];
  let i = len1;
  let j = len2;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && s1[i - 1] === s2[j - 1]) {
      changes.push({
        operation: 'equal',
        value: s1[i - 1],
      });
      i -= 1;
      j -= 1;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] <= dp[i - 1][j])) {
      changes.push({
        operation: 'insert',
        value: s2[j - 1],
      });
      j -= 1;
    } else if (i > 0 && (j === 0 || dp[i][j - 1] > dp[i - 1][j])) {
      changes.push({
        operation: 'remove',
        value: s1[i - 1],
      });
      i -= 1;
    }
  }

  // merge consecutive changes and add delimiter, skip last change
  const mergedChanges: Change[] = [];
  let lastChange: Change | undefined;
  for (const change of changes) {
    if (lastChange && lastChange.operation === change.operation) {
      lastChange.value = change.value + delimiter + lastChange.value;
    } else {
      lastChange = {
        operation: change.operation,
        value: change.value + delimiter,
      };
      mergedChanges.push(lastChange);
    }
  }
  mergedChanges.reverse();
  return mergedChanges;
}
